import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";
import { formatDateTime } from "../lib/datetime.js";
import { Badge, Button, Card, Input } from "../lib/ui.js";

interface OverviewRow {
  business_id: string;
  business_name: string;
  client_name: string;
  enabled: boolean;
  sender: string | null;
  last_error: string | null;
  last_error_at: string | null;
  sent_30d: number;
  failed_30d: number;
  pending: number;
  credits_30d: number;
}

interface MessageRow {
  id: string;
  kind: "confirmation" | "reminder" | "cancellation";
  changed: boolean;
  status: string;
  to_e164: string;
  starts_at: string;
  send_at: string;
  sent_at: string | null;
  error: string | null;
  credits_used: number | null;
  delivery_status: string | null;
}

const ERRORS: Record<string, string> = {
  missing_texto_api_key: "The TEXTO_API_KEY secret is not set on the Edge Functions.",
  not_a_mobile: "That is not an Australian mobile number.",
  sms_sender_invalid: "A sender can be at most 20 characters.",
};
const explain = (code: string) => ERRORS[code] ?? code;

/**
 * Admin > Text messages. Every client sends through the platform's one Texto account (the TEXTO_API_KEY secret) and each text says who it
 * is sent on behalf of. Here: the account's credits, which businesses are OFFERED texts, and what each has used. Nothing is sent until the
 * business's owner also turns confirmations or reminders on (Business settings > Text messages).
 */
export function Sms() {
  const [rows, setRows] = useState<OverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  async function load() {
    const { data, error: err } = await supabase.rpc("rpc_admin_sms_overview");
    if (err) setError(err.message);
    setRows((data ?? []) as OverviewRow[]);
    setLoading(false);
  }

  async function refreshCredits() {
    const res = await callFunction<{ credits: number }>("send-sms", { action: "balance" });
    if (res.ok) setCredits(Number(res.data.credits));
    else setNote({ ok: false, text: explain(res.error) });
  }

  useEffect(() => {
    void load();
    void refreshCredits();
  }, []);

  async function sendTest(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    const res = await callFunction<{ credits_used: number }>("send-sms", { action: "test", to: testTo });
    setBusy(false);
    if (!res.ok) {
      setNote({ ok: false, text: explain(res.error) });
      return;
    }
    setNote({ ok: true, text: `Test text sent (${String(res.data.credits_used)} credit).` });
    await refreshCredits();
  }

  if (loading) return <p className="rc-loading">Loading...</p>;
  const current = rows.find((r) => r.business_id === selected) ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h2 className="rc-h2" style={{ margin: 0 }}>Text messages</h2>
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "4px 0 0", maxWidth: 700 }}>
          Booking confirmations and reminders, sent through the platform's Texto account for every client. Each text opens "On behalf of
          &lt;business&gt;,". Texts are optional for every client: you offer them here, then the business's owner chooses which to turn on.
        </p>
      </div>
      {error && <p style={{ color: "var(--rc-danger)" }}>{error}</p>}

      <Card style={{ display: "flex", gap: 24, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ fontSize: 13 }}>
          <div style={{ color: "var(--rc-text-muted)" }}>Texto credits left</div>
          <strong style={{ fontSize: 22 }}>{credits === null ? "..." : credits}</strong>
        </div>
        <form onSubmit={sendTest} style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
            Send a test text to
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} style={{ width: 180 }} />
          </label>
          <Button type="submit" disabled={busy || !testTo.trim()}>
            {busy ? "Sending..." : "Send test"}
          </Button>
        </form>
        {note && <span style={{ fontSize: 13, color: note.ok ? "var(--rc-success)" : "var(--rc-danger)" }}>{note.text}</span>}
      </Card>

      <div className="rc-table-scroll">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 12px" }}>Business</th>
              <th style={{ padding: "10px 12px" }}>Status</th>
              <th style={{ padding: "10px 12px" }}>Last 30 days</th>
              <th style={{ padding: "10px 12px" }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.business_id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                <td style={{ padding: "10px 12px" }}>
                  {r.business_name}
                  <div style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>{r.client_name}</div>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  {r.enabled ? <Badge tone="success">Offered</Badge> : <Badge>Not offered</Badge>}
                  {r.last_error && (
                    <div style={{ fontSize: 12, color: "var(--rc-danger)", marginTop: 4 }} title={r.last_error_at ? formatDateTime(r.last_error_at) : undefined}>
                      {r.last_error}
                    </div>
                  )}
                </td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>
                  {r.sent_30d} sent ({Number(r.credits_30d)} credits)
                  {r.failed_30d > 0 ? `, ${r.failed_30d} failed` : ""}
                  {r.pending > 0 ? `, ${r.pending} waiting` : ""}
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>
                  <Button onClick={() => setSelected(r.business_id === selected ? null : r.business_id)}>{r.business_id === selected ? "Close" : "Manage"}</Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 16, color: "var(--rc-text-faint)" }}>
                  No businesses yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {current && <Manage key={current.business_id} row={current} onChanged={load} />}
    </div>
  );
}

function Manage({ row, onChanged }: { row: OverviewRow; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [offered, setOffered] = useState(row.enabled);
  const [sender, setSender] = useState(row.sender ?? "");
  const [messages, setMessages] = useState<MessageRow[]>([]);

  useEffect(() => {
    void supabase
      .from("sms_messages")
      .select("id, kind, changed, status, to_e164, starts_at, send_at, sent_at, error, credits_used, delivery_status")
      .eq("business_id", row.business_id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => setMessages((data ?? []) as MessageRow[]));
  }, [row.business_id]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    const { error } = await supabase.rpc("rpc_admin_set_sms", { p_business_id: row.business_id, p_enabled: offered, p_sender: sender });
    setBusy(false);
    if (error) {
      setNote({ ok: false, text: explain(error.message) });
      return;
    }
    setNote({ ok: true, text: "Saved." });
    await onChanged();
  }

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 className="rc-h2" style={{ margin: 0 }}>{row.business_name}</h2>
      <form onSubmit={save} style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={offered} onChange={(e) => setOffered(e.target.checked)} />
          Offer texts to this business
        </label>
        <label style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 4 }}>
          Sender override (leave blank to use the account's)
          <Input value={sender} maxLength={20} onChange={(e) => setSender(e.target.value)} placeholder="Account default" style={{ width: 240 }} />
        </label>
        <Button type="submit" variant="primary" disabled={busy}>
          Save
        </Button>
        {note && <span style={{ fontSize: 13, color: note.ok ? "var(--rc-success)" : "var(--rc-danger)" }}>{note.text}</span>}
      </form>

      <div>
        <h3 style={{ fontSize: 14, margin: "0 0 6px" }}>Recent messages</h3>
        <div className="rc-table-scroll">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--rc-text-muted)", borderBottom: "1px solid var(--rc-border)" }}>
                <th style={{ padding: "6px 8px" }}>Kind</th>
                <th style={{ padding: "6px 8px" }}>Booking at</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>Sent</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id} style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                  <td style={{ padding: "6px 8px" }}>{m.kind === "reminder" ? "Reminder" : m.kind === "cancellation" ? "Cancellation" : m.changed ? "Changed" : "Confirmation"}</td>
                  <td style={{ padding: "6px 8px" }}>{formatDateTime(m.starts_at)}</td>
                  <td style={{ padding: "6px 8px" }}>
                    {m.status}
                    {m.delivery_status ? ` (${m.delivery_status})` : ""}
                    {m.error ? <span style={{ color: "var(--rc-text-faint)" }}> - {m.error}</span> : null}
                  </td>
                  <td style={{ padding: "6px 8px" }}>{m.sent_at ? formatDateTime(m.sent_at) : `due ${formatDateTime(m.send_at)}`}</td>
                </tr>
              ))}
              {messages.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: 10, color: "var(--rc-text-faint)" }}>
                    Nothing yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
